import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../store/store";
import { ObjectUtils } from "../modules/utils";

interface IAxiosRequestConfig extends AxiosRequestConfig {
  isHeader?: boolean;
}

const BASE_URL = process.env.REACT_APP_GRAPH_API_URL;

const authHeader = (config: AxiosRequestConfig) => {
  const userToken = store.getState().user?.current?.accessToken;
  let headers = ObjectUtils.cleanObj({
    Authorization: `Bearer ${userToken}`,
    Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
  });
  return headers;
};

const getConfig = (config?: IAxiosRequestConfig) => {
  if (config && config.isHeader === false) {
    return {
      ...config,
      transformRequest: [
        (data: any, headers: any) => {
          delete headers.common.Authorization;
          delete headers.common.Prefer;
          return data;
        },
      ],
    };
  }
  return config;
};

class AxiosClient {
  private static instance: AxiosClient;
  private axios: AxiosInstance;

  private constructor() {
    this.axios = axios.create({
      baseURL: `${BASE_URL}/`,
      timeout: 20000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.axios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        config.headers = authHeader(config);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): AxiosClient {
    if (!AxiosClient.instance) {
      AxiosClient.instance = new AxiosClient();
    }
    return AxiosClient.instance;
  }

  public async get<T>(
    url: string,
    config?: IAxiosRequestConfig
  ): Promise<AxiosResponse> {
    const customizeConfig = getConfig(config);
    return await this.axios.get<T>(url, customizeConfig);
  }

  public async post<T>(
    url: string,
    data?: any,
    config?: IAxiosRequestConfig
  ): Promise<AxiosResponse> {
    const customizeConfig = getConfig(config);
    return await this.axios.post<T>(url, data, customizeConfig);
  }

  public async patch<T>(
    url: string,
    data?: any,
    config?: IAxiosRequestConfig
  ): Promise<AxiosResponse> {
    const customizeConfig = getConfig(config);
    return await this.axios.patch<T>(url, data, customizeConfig);
  }
  public async delete<T>(
    url: string,
    config?: IAxiosRequestConfig
  ): Promise<AxiosResponse> {
    const customizeConfig = getConfig(config);
    return await this.axios.patch<T>(url, customizeConfig);
  }
}

const axiosClient = AxiosClient.getInstance();

export default axiosClient;
